import { useEffect, useRef } from "react";
import "./CustomCursor.css";

const HOVER_SELECTOR =
  'a, button, [role="button"], label[for], summary, .cursor-hover';
const FIELD_SELECTOR = 'input, textarea, select, [contenteditable="true"], [contenteditable=""]';
const INVERT_SELECTOR = "[data-cursor-invert]";

export default function CustomCursor() {
  const rootRef = useRef(null);
  const plusRef = useRef(null);
  const echoRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    // Guard: fine pointer only. Touch devices get zero markup/CSS side effects.
    if (
      typeof window === "undefined" ||
      !window.matchMedia ||
      !window.matchMedia("(pointer: fine)").matches
    ) {
      return;
    }

    const reducedMotion =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const root = rootRef.current;
    const plus = plusRef.current;
    const echo = echoRef.current;
    const label = labelRef.current;
    if (!root || !plus || !echo || !label) return;

    document.documentElement.classList.add("plus-cursor");

    // Start off-screen so nothing flashes at (0,0) before first move.
    const target = { x: -100, y: -100 };
    const plusPos = { x: -100, y: -100 };
    const echoPos = { x: -100, y: -100 };
    let seenMove = false;
    let rafId = 0;

    const PLUS_LERP = reducedMotion ? 1 : 0.16;
    const ECHO_LERP = reducedMotion ? 1 : 0.07;

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
      seenMove = true;
    };

    // Evaluate hover/invert/field/label state from the element under the pointer.
    const onOver = (e) => {
      const el = e.target;
      if (!el || el.nodeType !== 1) return;

      const isField = !!el.closest(FIELD_SELECTOR);
      const invertEl = el.closest(INVERT_SELECTOR);
      const hoverEl = isField ? null : el.closest(HOVER_SELECTOR);
      const labelEl = hoverEl ? hoverEl.closest("[data-cursor-label]") : null;

      root.setAttribute("data-field", isField ? "true" : "false");
      root.setAttribute("data-invert", invertEl ? "true" : "false");
      root.setAttribute("data-hover", hoverEl ? "true" : "false");

      if (labelEl) {
        label.textContent = labelEl.getAttribute("data-cursor-label") || "";
        root.setAttribute("data-label-visible", "true");
      } else {
        root.setAttribute("data-label-visible", "false");
      }
    };

    const onDown = (e) => {
      if (reducedMotion) return;
      const inverted = root.getAttribute("data-invert") === "true";
      const ring = document.createElement("div");
      ring.className = "pc-pulse pc-pulse-animate" + (inverted ? " pc-pulse-invert" : "");
      ring.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      // recompose transform to keep the -50% centering
      ring.style.left = "0px";
      ring.style.top = "0px";
      ring.style.transform = `translate(-50%, -50%) translate(${e.clientX}px, ${e.clientY}px) scale(0.4)`;
      root.appendChild(ring);
      ring.addEventListener("animationend", () => ring.remove(), { once: true });
    };

    const loop = () => {
      plusPos.x += (target.x - plusPos.x) * PLUS_LERP;
      plusPos.y += (target.y - plusPos.y) * PLUS_LERP;
      echoPos.x += (target.x - echoPos.x) * ECHO_LERP;
      echoPos.y += (target.y - echoPos.y) * ECHO_LERP;

      plus.style.transform = `translate(-50%, -50%) translate(${plusPos.x}px, ${plusPos.y}px)`;
      echo.style.transform = `translate(-50%, -50%) translate(${echoPos.x}px, ${echoPos.y}px)`;
      if (seenMove) {
        label.style.transform = `translate(-50%, -50%) translate(${plusPos.x + 34}px, ${plusPos.y}px)`;
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver, { passive: true });
    window.addEventListener("mousedown", onDown, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mousedown", onDown);
      document.documentElement.classList.remove("plus-cursor");
    };
  }, []);

  return (
    <div className="pc-root" ref={rootRef} aria-hidden="true">
      <div className="pc-echo" ref={echoRef}>
        <div className="pc-glyph">
          <span className="pc-bar pc-bar-h" />
          <span className="pc-bar pc-bar-v" />
        </div>
      </div>
      <div className="pc-plus" ref={plusRef}>
        <div className="pc-glyph">
          <span className="pc-bar pc-bar-h" />
          <span className="pc-bar pc-bar-v" />
        </div>
      </div>
      <span className="pc-label" ref={labelRef} />
    </div>
  );
}
